/**
 * LoRA, VAE et embeddings — ceux de la machine qui générera, réglables image
 * par image. Un embedding (textual inversion) s'appelle par son mot-clé dans
 * le prompt ou le prompt négatif : le mot-clé est lu dans le fichier et
 * affiché, puisque c'est la personne qui doit l'écrire.
 *
 * La liste vient de cette machine (heartbeat de la machine appairée, ou
 * /api/components en local) : chaque LoRA y dit l'architecture lue dans son
 * propre fichier, chaque VAE les modèles qu'il sait décoder. Ce qui ne
 * convient pas au modèle choisi est grisé avec la raison, jamais retiré en
 * silence. La sélection vit dans le store et part avec la requête.
 */
import { toast } from "../../components/Toast.js";
import { h, setChildren } from "../../components/dom.js";
import {
	componentFits,
	machineComponents,
	parseEmbeddingInput,
	parseLoraInput,
	parseVaeInput,
	selectionKey,
} from "../../services/components.js";
import type { EmbeddingSelection, LoraSelection } from "../../state/store.js";
import { store } from "../../state/store.js";
import type { LocalComponent } from "../../types/index.js";

const ARCH_LABELS: Record<string, string> = {
	sd15: "SD1.5",
	sdxl: "SDXL",
	sd3: "SD3",
	flux: "FLUX",
	pixart_sigma: "PixArt-Σ",
	sana: "Sana",
	zimage: "Z-Image",
};

function archLabel(architectures: string[]): string {
	if (architectures.length === 0) return "architecture inconnue";
	return architectures.map((arch) => ARCH_LABELS[arch] ?? arch).join(" / ");
}

export function componentsPanel(): HTMLElement {
	let inventory: LocalComponent[] = [];
	let loadToken = 0;

	const loraList = h("div", { class: "component-list" });
	const vaeBox = h("div", { class: "component-list" });
	const embeddingList = h("div", { class: "component-list" });
	const embeddingInput = h("input", {
		type: "text",
		placeholder: "auteur/nom, ou fichier.safetensors ; =mot-clé pour le choisir",
		maxLength: 400,
	}) as HTMLInputElement;
	const status = h("p", { class: "help-text" });

	const loraInput = h("input", {
		type: "text",
		placeholder: "auteur/nom, ou auteur/nom::fichier.safetensors",
		maxLength: 400,
	}) as HTMLInputElement;
	const vaeInput = h("input", {
		type: "text",
		placeholder: "auteur/nom, ou auteur/nom:vae",
		maxLength: 300,
	}) as HTMLInputElement;

	function addTypedLora(): void {
		const parsed = parseLoraInput(loraInput.value);
		if (!parsed) {
			toast("Indiquez un dépôt Hugging Face (auteur/nom) ou un fichier .safetensors.", "warning");
			return;
		}
		const key = selectionKey(parsed);
		if (store.get().loras.some((lora) => selectionKey(lora) === key)) return;
		const known = inventory.find((c) => c.kind === "lora" && selectionKey(c) === key);
		store.setState({
			loras: [
				...store.get().loras,
				{
					...parsed,
					weight: 0.8,
					name: known?.name ?? parsed.source,
					architectures: known?.architectures ?? [],
				},
			],
		});
		loraInput.value = "";
		if (!known) {
			toast(
				"Ce LoRA n'est pas encore sur votre ordinateur : il le téléchargera à la première image.",
				"info"
			);
		}
	}

	function useTypedVae(): void {
		const parsed = parseVaeInput(vaeInput.value);
		if (!parsed) {
			toast("Indiquez un dépôt Hugging Face (auteur/nom, ou auteur/nom:sous-dossier).", "warning");
			return;
		}
		store.setState({ vae: parsed });
		vaeInput.value = "";
	}

	function addTypedEmbedding(): void {
		const parsed = parseEmbeddingInput(embeddingInput.value);
		if (!parsed) {
			toast(
				"Indiquez un dépôt Hugging Face (auteur/nom) ou un fichier .safetensors, et =mot-clé si besoin.",
				"warning"
			);
			return;
		}
		const key = selectionKey(parsed);
		if (store.get().embeddings.some((entry) => selectionKey(entry) === key)) return;
		const known = inventory.find((c) => c.kind === "embedding" && selectionKey(c) === key);
		const token = parsed.token || known?.token || parsed.source.split("/").pop() || parsed.source;
		store.setState({
			embeddings: [
				...store.get().embeddings,
				{
					source: parsed.source,
					weight_name: parsed.weight_name ?? null,
					token,
					name: known?.name ?? parsed.source,
					architectures: known?.architectures ?? [],
					vectors: known?.vectors ?? null,
				},
			],
		});
		embeddingInput.value = "";
		if (!known) {
			toast(
				`Pas encore sur votre ordinateur : il le téléchargera (quelques Ko). Écrivez « ${token} » dans la description ; votre ordinateur confirmera le mot-clé lu dans le fichier.`,
				"info"
			);
		}
	}

	function setEmbedding(entry: EmbeddingSelection, on: boolean): void {
		const key = selectionKey(entry);
		const others = store.get().embeddings.filter((e) => selectionKey(e) !== key);
		store.setState({ embeddings: on ? [...others, entry] : others });
	}

	function embeddingRow(entry: EmbeddingSelection, selected: boolean, fits: boolean): HTMLElement {
		const checkbox = h("input", { type: "checkbox" }) as HTMLInputElement;
		checkbox.checked = selected;
		checkbox.disabled = !fits && !selected;
		checkbox.addEventListener("change", () => setEmbedding(entry, checkbox.checked));
		const size = entry.vectors ? ` · ${entry.vectors} vecteur${entry.vectors > 1 ? "s" : ""}` : "";
		return h(
			"div",
			{ class: fits ? "lora-item" : "lora-item component-unfit" },
			checkbox,
			h(
				"div",
				null,
				h("div", { class: "lora-name", title: selectionKey(entry) }, entry.name),
				h(
					"div",
					{ class: "component-meta" },
					fits
						? `${archLabel(entry.architectures)}${size} · à écrire dans le prompt ou le négatif : `
						: `fait pour ${archLabel(entry.architectures)} : ne convient pas à ce modèle · `,
					h("code", { class: "embedding-token" }, entry.token)
				)
			)
		);
	}

	function setLora(entry: LoraSelection, on: boolean): void {
		const key = selectionKey(entry);
		const others = store.get().loras.filter((lora) => selectionKey(lora) !== key);
		store.setState({ loras: on ? [...others, entry] : others });
	}

	function setWeight(key: string, weight: number): void {
		store.setState({
			loras: store
				.get()
				.loras.map((lora) => (selectionKey(lora) === key ? { ...lora, weight } : lora)),
		});
	}

	function loraRow(entry: LoraSelection, selected: boolean, fits: boolean): HTMLElement {
		const key = selectionKey(entry);
		const checkbox = h("input", { type: "checkbox" }) as HTMLInputElement;
		checkbox.checked = selected;
		checkbox.disabled = !fits && !selected;
		checkbox.addEventListener("change", () => setLora(entry, checkbox.checked));

		const weight = h("input", {
			type: "range",
			min: "-1",
			max: "2",
			step: "0.05",
			value: String(entry.weight),
			disabled: !selected,
			"aria-label": `Poids de ${entry.name}`,
		}) as HTMLInputElement;
		const badge = h("span", { class: "value-badge" }, entry.weight.toFixed(2));
		weight.addEventListener("input", () => {
			badge.textContent = Number(weight.value).toFixed(2);
		});
		weight.addEventListener("change", () => setWeight(key, Number(weight.value)));

		return h(
			"div",
			{ class: fits ? "lora-item" : "lora-item component-unfit" },
			checkbox,
			h(
				"div",
				null,
				h("div", { class: "lora-name", title: key }, entry.name),
				h(
					"div",
					{ class: "component-meta" },
					fits
						? archLabel(entry.architectures)
						: `fait pour ${archLabel(entry.architectures)} : ne convient pas à ce modèle`
				)
			),
			h("div", { class: "range-with-value" }, weight, badge)
		);
	}

	function render(): void {
		const architecture = store.get().selectedModel?.architecture;
		const selected = store.get().loras;
		const selectedKeys = new Set(selected.map(selectionKey));

		// --- LoRA : ceux de la machine, puis ceux tapés à la main ---
		const machineLoras: LoraSelection[] = inventory
			.filter((c) => c.kind === "lora")
			.map((c) => {
				const chosen = selected.find((lora) => selectionKey(lora) === selectionKey(c));
				return {
					source: c.source,
					weight_name: c.weight_name ?? null,
					weight: chosen?.weight ?? 0.8,
					name: c.name,
					architectures: c.architectures,
				};
			});
		const machineKeys = new Set(machineLoras.map(selectionKey));
		const typed = selected.filter((lora) => !machineKeys.has(selectionKey(lora)));
		const rows = [...machineLoras, ...typed].map((entry) =>
			loraRow(
				entry,
				selectedKeys.has(selectionKey(entry)),
				componentFits(entry.architectures, architecture)
			)
		);
		setChildren(
			loraList,
			...(rows.length > 0
				? rows
				: [h("p", { class: "muted" }, "Aucun LoRA sur votre ordinateur.")]),
			h(
				"div",
				{ class: "component-add" },
				loraInput,
				h("button", { class: "btn", type: "button", onclick: addTypedLora }, "Ajouter")
			)
		);

		// --- Embeddings : ceux de la machine, puis ceux tapés à la main ---
		const chosenEmbeddings = store.get().embeddings;
		const chosenKeys = new Set(chosenEmbeddings.map(selectionKey));
		const machineEmbeddings: EmbeddingSelection[] = inventory
			.filter((c) => c.kind === "embedding")
			.map((c) => ({
				source: c.source,
				weight_name: c.weight_name ?? null,
				token: c.token ?? c.name,
				name: c.name,
				architectures: c.architectures,
				vectors: c.vectors ?? null,
			}));
		const machineEmbeddingKeys = new Set(machineEmbeddings.map(selectionKey));
		const embeddingRows = [
			...machineEmbeddings,
			...chosenEmbeddings.filter((e) => !machineEmbeddingKeys.has(selectionKey(e))),
		].map((entry) =>
			embeddingRow(
				entry,
				chosenKeys.has(selectionKey(entry)),
				componentFits(entry.architectures, architecture)
			)
		);
		setChildren(
			embeddingList,
			...(embeddingRows.length > 0
				? embeddingRows
				: [h("p", { class: "muted" }, "Aucun embedding sur votre ordinateur.")]),
			h(
				"div",
				{ class: "component-add" },
				embeddingInput,
				h("button", { class: "btn", type: "button", onclick: addTypedEmbedding }, "Ajouter")
			)
		);

		// --- VAE : celui du modèle, ou un autre de la même famille ---
		const current = store.get().vae;
		const vaes = inventory.filter((c) => c.kind === "vae");
		const select = h("select", { "aria-label": "VAE" }) as HTMLSelectElement;
		select.appendChild(h("option", { value: "" }, "VAE du modèle"));
		for (const vae of vaes) {
			const fits = componentFits(vae.architectures, architecture);
			const option = h(
				"option",
				{ value: selectionKey({ source: vae.source, weight_name: vae.subfolder }) },
				fits ? vae.name : `${vae.name} (fait pour ${archLabel(vae.architectures)})`
			) as HTMLOptionElement;
			option.disabled = !fits;
			select.appendChild(option);
		}
		const currentKey = current
			? selectionKey({ source: current.source, weight_name: current.subfolder })
			: "";
		if (
			current &&
			!vaes.some((v) => selectionKey({ source: v.source, weight_name: v.subfolder }) === currentKey)
		) {
			select.appendChild(h("option", { value: currentKey }, current.name));
		}
		select.value = currentKey;
		select.addEventListener("change", () => {
			if (!select.value) {
				store.setState({ vae: null });
				return;
			}
			const vae = vaes.find(
				(v) => selectionKey({ source: v.source, weight_name: v.subfolder }) === select.value
			);
			if (vae) {
				store.setState({
					vae: {
						source: vae.source,
						subfolder: vae.subfolder ?? null,
						name: vae.name,
						architectures: vae.architectures,
					},
				});
			}
		});
		const vaeUnfit = current && !componentFits(current.architectures, architecture);
		setChildren(
			vaeBox,
			h("div", { class: "form-group" }, select),
			vaeUnfit
				? h(
						"p",
						{ class: "component-meta" },
						`« ${current.name} » ne convient pas à ce modèle : le sien sera utilisé.`
					)
				: null,
			h(
				"div",
				{ class: "component-add" },
				vaeInput,
				h("button", { class: "btn", type: "button", onclick: useTypedVae }, "Utiliser")
			)
		);
	}

	async function reload(): Promise<void> {
		const token = ++loadToken;
		try {
			const found = await machineComponents();
			if (token !== loadToken) return;
			status.textContent = "";
			// The machines are polled: redraw only for a real change, or a
			// weight slider being dragged would jump back under the cursor.
			if (JSON.stringify(found) === JSON.stringify(inventory)) return;
			inventory = found;
		} catch {
			if (token !== loadToken) return;
			inventory = [];
			status.textContent =
				"La liste des LoRA, VAE et embeddings de votre ordinateur est indisponible pour l'instant.";
		}
		render();
	}

	store.subscribe((s) => s.selectedModel, render);
	store.subscribe((s) => s.loras, render);
	store.subscribe((s) => s.vae, render);
	store.subscribe((s) => s.embeddings, render);
	store.subscribe(
		(s) => s.executionTarget,
		() => void reload()
	);
	store.subscribe(
		(s) => s.workers,
		() => void reload()
	);
	store.subscribe(
		(s) => s.status?.generates_here ?? null,
		() => void reload()
	);
	render();
	void reload();

	return h(
		"div",
		{ class: "card library-components" },
		h("div", { class: "card-title" }, "LoRA, VAE et embeddings"),
		h(
			"p",
			{ class: "help-text" },
			"Ceux de votre ordinateur, lus dans leurs fichiers. Ils s'appliquent à la prochaine image, et seulement à elle."
		),
		h("h4", { class: "component-heading" }, "LoRA"),
		loraList,
		h("h4", { class: "component-heading" }, "VAE"),
		vaeBox,
		h("h4", { class: "component-heading" }, "Embeddings (textual inversion)"),
		embeddingList,
		status
	);
}
